/**
 * MapSpec encoding/filters → MapLibre data-driven expressions, plus the legend
 * model derived from the same inputs. This module is the shared source of truth
 * so editor and embed produce byte-identical output. Expressions are plain
 * JSON arrays (typed loosely as `MapExpr`) — no MapLibre import — so the builder
 * is pure and unit-testable without a browser.
 */

import type { ColorBy, FilterClause, IconBy, SizeBy } from "./mapspec";
import { categoryColoring, numericBreaks, type CategoryColoring, type CategoryEntry } from "./color";
import { shapeAssignment, type ShapeEntry } from "./shape";

/** A MapLibre expression (JSON array) or a literal value. */
export type MapExpr = unknown;

const get = (col: string): MapExpr => ["get", col];
const toNum = (col: string): MapExpr => ["to-number", ["get", col], 0];

/**
 * Categorical color expression: match the column value to its assigned color,
 * with the Other bucket as the fallback. Returns the fallback color as a plain
 * string when there are no explicit assignments (nothing to match).
 */
export function categoricalColorExpression(column: string, coloring: CategoryColoring): MapExpr {
  const pairs: MapExpr[] = [];
  for (const [value, color] of Object.entries(coloring.colorFor)) {
    pairs.push(value, color);
  }
  if (pairs.length === 0) return coloring.other;
  return ["match", get(column), ...pairs, coloring.other];
}

/** Sequential numeric color ramp across computed breaks. */
export function numericColorExpression(column: string, breaks: number[], ramp: string[]): MapExpr {
  const stops: MapExpr[] = [];
  const n = Math.min(breaks.length, ramp.length);
  for (let i = 0; i < n; i++) stops.push(breaks[i], ramp[i]);
  return ["interpolate", ["linear"], toNum(column), ...stops];
}

/**
 * Build the circle-color expression for the active color-by. `values` is the
 * column's values across all points; `ramp` the sequential ramp; `fallback` the
 * default marker fill when there's no color-by.
 */
export function colorExpression(
  colorBy: ColorBy | undefined,
  values: string[],
  palette: string[],
  paletteOther: string,
  ramp: string[],
  fallback: string,
): MapExpr {
  if (!colorBy) return fallback;
  if (colorBy.mode === "numeric") {
    const nums = values.map((v) => Number(v)).filter((n) => Number.isFinite(n));
    return numericColorExpression(colorBy.column, numericBreaks(nums, ramp.length), ramp);
  }
  const coloring = categoryColoring(values, palette, paletteOther, colorBy.overrides);
  return categoricalColorExpression(colorBy.column, coloring);
}

/**
 * Size-by expression: interpolate circle-radius across the value domain, clamped
 * to [minPx, maxPx] so outliers don't blow out. Falls back to a constant radius.
 */
export function sizeExpression(
  sizeBy: SizeBy | undefined,
  values: string[],
  pxRange: [number, number],
  fallbackPx: number,
): MapExpr {
  if (!sizeBy) return fallbackPx;
  const nums = values.map((v) => Number(v)).filter((n) => Number.isFinite(n));
  if (nums.length === 0) return fallbackPx;
  const lo = sizeBy.min ?? Math.min(...nums);
  const hi = sizeBy.max ?? Math.max(...nums);
  const [minPx, maxPx] = pxRange;
  if (lo === hi) return (minPx + maxPx) / 2;
  return ["interpolate", ["linear"], toNum(sizeBy.column), lo, minPx, hi, maxPx];
}

/** One filter clause → a boolean MapLibre expression. */
export function clauseExpression(clause: FilterClause): MapExpr {
  const { column, op, value } = clause;
  const val = get(column);
  const coalesced: MapExpr = ["coalesce", get(column), ""];
  switch (op) {
    case "is":
      return ["==", val, value ?? ""];
    case "is_not":
      return ["!=", val, value ?? ""];
    case "in":
      return ["in", val, ["literal", Array.isArray(value) ? value : []]];
    case "gte":
      return [">=", toNum(column), Number(value ?? 0)];
    case "lte":
      return ["<=", toNum(column), Number(value ?? 0)];
    case "between": {
      const [a, b] = Array.isArray(value) ? value : [0, 0];
      return ["all", [">=", toNum(column), Number(a)], ["<=", toNum(column), Number(b)]];
    }
    case "contains":
      return ["in", String(value ?? ""), ["to-string", val]];
    case "empty":
      return ["==", coalesced, ""];
    case "not_empty":
      return ["!=", coalesced, ""];
    default:
      return true;
  }
}

/** AND-combine all filter clauses; null when there are none (show everything). */
export function filterExpression(filters: FilterClause[]): MapExpr | null {
  if (filters.length === 0) return null;
  if (filters.length === 1) return clauseExpression(filters[0]!);
  return ["all", ...filters.map(clauseExpression)];
}

export type LegendModel =
  | { kind: "none" }
  | { kind: "categorical"; column: string; entries: CategoryEntry[] }
  | { kind: "numeric"; column: string; stops: { value: number; color: string }[] };

/** Legend model reflecting the active color-by (drives the generated legend UI). */
export function legendModel(
  colorBy: ColorBy | undefined,
  values: string[],
  palette: string[],
  paletteOther: string,
  ramp: string[],
): LegendModel {
  if (!colorBy) return { kind: "none" };
  if (colorBy.mode === "numeric") {
    const nums = values.map((v) => Number(v)).filter((n) => Number.isFinite(n));
    const breaks = numericBreaks(nums, ramp.length);
    return {
      kind: "numeric",
      column: colorBy.column,
      stops: breaks.map((value, i) => ({ value, color: ramp[Math.min(i, ramp.length - 1)]! })),
    };
  }
  const coloring = categoryColoring(values, palette, paletteOther, colorBy.overrides);
  return { kind: "categorical", column: colorBy.column, entries: coloring.entries };
}

export type ShapeLegendModel =
  | { kind: "none" }
  | { kind: "shape"; column: string; entries: ShapeEntry[] };

/** Legend model reflecting the active shape-by (MAP-24 — drives the shape glyphs). */
export function shapeLegendModel(iconBy: IconBy | undefined, values: string[]): ShapeLegendModel {
  if (!iconBy) return { kind: "none" };
  const assignment = shapeAssignment(values);
  return { kind: "shape", column: iconBy.column, entries: assignment.entries };
}

/**
 * True when color-by and shape-by can share ONE legend block (glyphs tinted by
 * color): same column, categorical color. Their value sets and frequency
 * ordering are then identical, so each row shows a value's shape AND color. When
 * false (different columns, or numeric color) the two render as separate blocks.
 */
export function legendsMerge(colorBy: ColorBy | undefined, iconBy: IconBy | undefined): boolean {
  return (
    !!colorBy &&
    !!iconBy &&
    colorBy.mode === "categorical" &&
    colorBy.column === iconBy.column
  );
}
